import { useEffect } from 'react';
import moment from 'moment';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import DialogContent from '@mui/material/DialogContent';
import { FormControl, FormLabel, FormHelperText } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import { ERROR_MESSAGES } from 'src/constants';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import './save-scenario-dialog.scss';
import UniqueNameValidator from '../../../../../../hooks/unique-name-validator.hook';
import { getListOfSavedScenarioNames } from '../../store/pricing-simulator-slice';

export interface SimpleDialogProps {
  open: boolean;
  onClose: any;
}

export interface ScenarioInfo {
  name: string;
  description: string;
}

function SaveScenarioDialog(props: SimpleDialogProps) {
  const { onClose, open } = props;
  const appDispatch = useAppDispatch();
  const sourceId = useAppSelector((state) => state.user.source);

  useEffect(() => {
    appDispatch(getListOfSavedScenarioNames(sourceId));
  }, []);

  const scenarioNameList = useAppSelector(
    (state) => state.pricingSimulator.listOfScenariosName
  );

  /* eslint-disable no-use-before-define */
  /* eslint-disable no-unneeded-ternary */

  const handleClose = (formValue: ScenarioInfo) => {
    onClose({
      name: formValue.name,
      description: formValue.description,
      duplicateName: uniqueName.length ? true : false,
      simulationId: uniqueName.length
        ? scenarioNameList.filter(
            (data) => data.name.toLowerCase() === formValue.name.toLowerCase()
          )[0].simulation_id
        : false
    });
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      description: ''
    },
    validationSchema: Yup.object({
      name: Yup.string().required(ERROR_MESSAGES.REQUIRED),
      description: Yup.string().max(200, 'Too Long!')
    }),
    onSubmit: handleClose
  });

  const uniqueName = UniqueNameValidator({
    list: scenarioNameList,
    name: formik.values.name
  });

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        Save Scenario
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormControl sx={{ width: '100%' }} variant='standard' key='name'>
          <FormLabel htmlFor='name'>Name</FormLabel>
          <TextField
            fullWidth
            size='small'
            autoFocus
            value={formik.values.name}
            error={formik.touched.name && !!formik.errors.name}
            margin='dense'
            id='name'
            type='text'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <FormHelperText>
            {formik.touched.name ? formik.errors.name || '' : ''}
          </FormHelperText>
        </FormControl>
        <FormControl
          key='description'
          sx={{ width: '100%' }}
          variant='standard'
          style={{ paddingTop: '15px' }}
        >
          <FormLabel htmlFor='description'>Comments(Optional)</FormLabel>
          <TextField
            id='description'
            multiline
            rows={6}
            margin='dense'
            placeholder='Description of scenario'
            value={formik.values.description}
            type='text'
            onChange={formik.handleChange}
            error={formik.touched.description && !!formik.errors.description}
            onBlur={formik.handleBlur}
          />
          <div className='character-count-container'>
            {formik.values.description.length}/
            {200 - formik.values.description.length}
          </div>
        </FormControl>
        {uniqueName.length ? (
          <div className='error-message'>
            {formik.values.name} already exists.
          </div>
        ) : (
          ''
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          disabled={
            !formik.dirty ||
            !formik.isValid ||
            (uniqueName.length > 0 ? true : false)
          }
          onClick={formik.submitForm}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SaveScenarioDialog;
